import React, {FC, useEffect, useState} from 'react';
import {RestfulProvider} from 'restful-react';
import {useLocalStorage} from '../hooks/useLocalStorage';
import {Spin} from 'antd';
import {wakHandleRequest, wakLogout} from '../api-wak';
import {INDUS_CLIENT_ID} from './Login';

const SESSION_KEY = 'interson-common-session';

export interface Session {
  token: string;
  expires?: string /** @desc ISO 8601 string */;
  remember: boolean;
  user: {
    raisonSociale: string;
    identifiant: string;
    code: string;
    id_intersonProtac: string;
    id_emboutFrancais: string;
    id_accessoire: string;
    ID: string;
  };
}

export const SessionContext = React.createContext<
  [
    Session | undefined,
    React.Dispatch<React.SetStateAction<Session | undefined>>,
  ]
>([undefined, () => {}]);

export const SessionProvider: FC = (props) => {
  const [sessionStored, setSessionStored] = useLocalStorage(SESSION_KEY);
  const [session, setSession] = useState<Session | undefined>(
    sessionStored ? JSON.parse(sessionStored) : undefined,
  );

  // listen on event "unauthorized" that is triggered by any 401 response from server (except login)
  useEffect(() => {
    const handleUnauthorized = () => {
      setSession(undefined);
      setSessionStored(undefined);
      window.location.href = '/login';
    };
    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (session) {
      (async () => {
        let user;
        try {
          const resUser = await wakHandleRequest('Utils', 'getUser');
          user = (await resUser.json())?.result;
        } catch (err) {
          setSession(undefined);
          setSessionStored(undefined);
          await wakLogout();
          window.location.href = '/login';
          return;
        }

        if (
          user.ID === INDUS_CLIENT_ID ||
          // mismatch between cookie and user stored in localStorage
          user.ID !== session.user.ID
        ) {
          setSession(undefined);
          setSessionStored(undefined);
          await wakLogout();
          window.location.href = '/login';
        }
      })();
    }
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SessionContext.Provider
      value={[
        session,
        (session) => {
          setSessionStored(session ? JSON.stringify(session) : undefined);
          setSession(session);
        },
      ]}
    >
      <RestfulProvider
        base={process.env.REACT_APP_API_HOST ?? ''}
        onError={(err) => {
          if (err.status === 401) {
            window.dispatchEvent(new Event('unauthorized'));
          }
        }}
        requestOptions={{
          credentials: 'include',
        }}
      >
        {props.children}
      </RestfulProvider>
    </SessionContext.Provider>
  );
};
